import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { ArrowLeft, CalendarDays } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { ContactForm } from "@/components/ContactForm";
import { publicApi } from "@/lib/api";
import { communityImg } from "@/pages/Communities";

export default function CommunityPage() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => { publicApi.get(`/public/communities/${slug}`).then((r) => setC(r.data)).catch(() => setMissing(true)); }, [slug]);
  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="community-page">
      <SiteHeader />
      {missing && <main className="px-5 pb-24 pt-36 text-center"><h1 className="text-3xl font-bold text-slate-900" data-testid="community-not-found">We couldn't find this community</h1><Link to="/communities" className="mt-4 inline-block font-semibold text-[#1D64B4]">See all communities</Link></main>}
      {c && (
        <>
          <section className="relative h-[44vh] min-h-[300px] overflow-hidden bg-[#0A3A6B] pt-[72px]">
            <img src={communityImg(c)} alt={c.name} className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A3A6B] via-[#0A3A6B]/50 to-transparent" />
            <div className="relative mx-auto flex h-full max-w-5xl flex-col justify-end px-5 pb-10 lg:px-10">
              <Link to="/communities" className="mb-4 flex w-fit items-center gap-1 text-sm text-sky-100 hover:text-white" data-testid="community-back-link"><ArrowLeft className="h-4 w-4" /> All communities</Link>
              <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="community-title">{c.name}</h1>
              <p className="mt-2 text-sky-100">{c.tagline}</p>
            </div>
          </section>
          <main className="mx-auto grid max-w-5xl gap-8 px-5 py-12 lg:grid-cols-[1.5fr_1fr] lg:px-10">
            <div className="space-y-8">
              <p className="whitespace-pre-line text-base leading-relaxed text-slate-700" data-testid="community-description">{c.description}</p>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Upcoming gatherings</h2>
                {c.events.length === 0 && <p className="mt-2 text-sm text-slate-500" data-testid="community-no-events">New gatherings will be posted here soon.</p>}
                <div className="mt-3 space-y-2">
                  {c.events.map((e) => (
                    <Link key={e.id} to={`/events/${e.id}`} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 hover:border-[#1D64B4]" data-testid={`community-event-${e.id}`}>
                      <CalendarDays className="h-5 w-5 text-[#1D64B4]" /><div><p className="font-medium text-slate-900">{e.title}</p><p className="text-xs text-slate-500">{format(parseISO(e.date), "EEE, MMM d")}{e.location && ` · ${e.location}`}</p></div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            <aside className="order-first h-fit rounded-3xl border border-slate-200 bg-white p-6 lg:order-none lg:sticky lg:top-24">
              <h2 className="text-lg font-semibold text-slate-900">We'd love to have you</h2>
              <p className="mb-4 mt-1 text-sm text-slate-500">Leave your details and someone from the community will reach out.</p>
              <ContactForm path={`/public/communities/${c.id}/join`} cta="I'd like to join" done="Thank you! Someone from the community will reach out to you soon." testPrefix="community-join" />
            </aside>
          </main>
        </>
      )}
      <SiteFooter />
    </div>
  );
}
